/**
 * ProjectionPanel — shows projected P&L, BS, CF as compact tables
 * with change-vs-base indicators.
 */

import { cn } from '../../lib/utils'
import { AnimatedNumber } from '../finance/AnimatedNumber'
import { AlertTriangle } from 'lucide-react'
import type { ProjectionResult, BaseActuals } from '../../services/projectionEngine'

interface ProjectionPanelProps {
  projection: ProjectionResult | null
  baseActuals: BaseActuals | null
  className?: string
}

function pctChange(projected: number, base: number): string | null {
  if (!base || base === 0) return null
  const pct = ((projected - base) / Math.abs(base)) * 100
  return `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%`
}

function changeColor(projected: number, base: number, invertedBetter = false): string {
  if (!base) return 'text-[var(--text-muted,#64748B)]'
  const diff = projected - base
  const isPositive = invertedBetter ? diff < 0 : diff > 0
  if (Math.abs(diff) < 0.01) return 'text-[var(--text-muted,#64748B)]'
  return isPositive ? 'text-emerald-400' : 'text-red-400'
}

interface LineItem {
  label: string
  value: number
  base?: number
  prefix?: string
  suffix?: string
  decimals?: number
  compact?: boolean
  isBold?: boolean
  invertedBetter?: boolean
}

function LineRow({ item }: { item: LineItem }) {
  const { label, value, base, prefix = '$', suffix = '', decimals = 0, compact = true, isBold, invertedBetter } = item
  const change = base != null ? pctChange(value, base) : null

  return (
    <div className={cn(
      'flex items-center justify-between py-1',
      isBold && 'font-semibold',
    )}>
      <span className="text-[11px] text-[var(--text-secondary,#94A3B8)]">{label}</span>
      <div className="flex items-center gap-2">
        <AnimatedNumber
          value={value}
          prefix={prefix}
          suffix={suffix}
          decimals={decimals}
          compact={compact}
          className="text-[12px] font-mono text-[var(--text-primary,#F0F2F5)]"
        />
        {change && (
          <span className={cn('text-[9px] font-mono', changeColor(value, base!, invertedBetter))}>
            {change}
          </span>
        )}
      </div>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-4">
      <h4 className="text-[10px] uppercase tracking-[0.8px] text-[var(--text-muted,#64748B)] mb-2 flex items-center gap-1.5">
        <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent,var(--accent))]" />
        {title}
      </h4>
      <div className={cn(
        'rounded-lg border border-white/[0.06] bg-[var(--bg-surface,var(--bg-surface))]',
        'px-3 py-2',
      )}>
        {children}
      </div>
    </div>
  )
}

export function ProjectionPanel({ projection, baseActuals, className }: ProjectionPanelProps) {
  if (!projection) {
    return (
      <div className={cn('flex items-center justify-center py-16', className)}>
        <p className="text-[13px] text-[var(--text-muted,#64748B)]">
          Loading base period data...
        </p>
      </div>
    )
  }

  const { pl, bs, cf, kpis, warnings } = projection
  const base = baseActuals

  return (
    <div className={cn('flex flex-col', className)}>
      {/* Warnings */}
      {warnings.length > 0 && (
        <div className="mb-4 flex flex-col gap-2">
          {warnings.map((w, i) => (
            <div
              key={i}
              className={cn(
                'flex items-start gap-2 px-3 py-2 rounded-lg',
                'bg-amber-500/10 border border-amber-500/20',
              )}
            >
              <AlertTriangle className="h-3.5 w-3.5 text-amber-400 shrink-0 mt-0.5" />
              <p className="text-[11px] text-amber-300">{w}</p>
            </div>
          ))}
        </div>
      )}

      {/* P&L */}
      <Section title="Income Statement">
        <LineRow item={{ label: 'Revenue', value: pl.revenue, base: base?.revenue, isBold: true }} />
        <LineRow item={{ label: 'COGS', value: pl.cogs, base: base?.cogs, invertedBetter: true }} />
        <LineRow item={{ label: 'Gross Profit', value: pl.gross_profit, isBold: true }} />
        <LineRow item={{ label: 'Gross Margin', value: pl.gross_margin, prefix: '', suffix: '%', decimals: 1, compact: false }} />
        <div className="border-t border-white/[0.04] my-1" />
        <LineRow item={{ label: 'Operating Expenses', value: pl.operating_expenses, base: base?.operating_expenses, invertedBetter: true }} />
        <LineRow item={{ label: 'Operating Income', value: pl.operating_income }} />
        <LineRow item={{ label: 'Interest Expense', value: pl.interest_expense, invertedBetter: true }} />
        <LineRow item={{ label: 'Tax', value: pl.tax_expense, invertedBetter: true }} />
        <div className="border-t border-white/[0.04] my-1" />
        <LineRow item={{ label: 'Net Income', value: pl.net_income, base: base?.net_income, isBold: true }} />
        <LineRow item={{ label: 'Net Margin', value: pl.net_margin, prefix: '', suffix: '%', decimals: 1, compact: false }} />
      </Section>

      {/* BS */}
      <Section title="Balance Sheet">
        <LineRow item={{ label: 'Total Assets', value: bs.total_assets, base: base?.total_assets }} />
        <LineRow item={{ label: 'Working Capital', value: bs.working_capital, base: base?.working_capital, isBold: true }} />
        <LineRow item={{ label: 'Current Ratio', value: bs.current_ratio, prefix: '', suffix: 'x', decimals: 2, compact: false }} />
        <LineRow item={{ label: 'Accounts Receivable', value: bs.ar_balance, base: base?.ar_balance }} />
        <LineRow item={{ label: 'Accounts Payable', value: bs.ap_balance, base: base?.ap_balance }} />
      </Section>

      {/* CF */}
      <Section title="Cash Flow">
        <LineRow item={{ label: 'Starting Cash', value: cf.starting_cash }} />
        <LineRow item={{ label: 'Operating CF', value: cf.operating_cf }} />
        <LineRow item={{ label: 'Investing CF', value: cf.investing_cf }} />
        <div className="border-t border-white/[0.04] my-1" />
        <LineRow item={{ label: 'Ending Cash', value: cf.ending_cash, isBold: true }} />
      </Section>

      {/* KPIs row */}
      <div className="grid grid-cols-3 gap-2">
        {[
          { label: 'DSO', value: kpis.dso, suffix: ' days', prefix: '' },
          { label: 'DPO', value: kpis.dpo, suffix: ' days', prefix: '' },
          { label: 'CCC', value: kpis.cash_conversion_cycle, suffix: ' days', prefix: '' },
        ].map(kpi => (
          <div
            key={kpi.label}
            className={cn(
              'rounded-lg border border-white/[0.06] bg-[var(--bg-surface,var(--bg-surface))]',
              'px-3 py-2 text-center',
            )}
          >
            <p className="text-[9px] uppercase tracking-wider text-[var(--text-hint,#475569)]">{kpi.label}</p>
            <p className="text-[16px] font-mono font-semibold text-[var(--text-primary,#F0F2F5)]">
              {kpi.prefix}{kpi.value}{kpi.suffix}
            </p>
          </div>
        ))}
      </div>
    </div>
  )
}
